import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/useThemeContext";
import {
  selectAuthAdmin,
  selectAuthToken,
  useAuthStore,
} from "../../store/useAuth";
import { useBranchContext } from "../../context/useBranchContext";
import { Product, Branch } from "../../types";
import DiscountModal from "../discount-modal/discount-modal";
import Icon from "../icon/icon";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { buildApiUrl } from "../../packages/hooks/hook-utils";
import { handleHttpError } from "../../utils/error-utils";
import { toast } from "sonner";
import "./header.css";

// Header component props interface
export interface HeaderProps {
  products: Product[];
  onProductsUpdate: () => Promise<void>;
}

const Header: React.FC<HeaderProps> = ({ products, onProductsUpdate }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const user = useAuthStore(selectAuthAdmin);
  const token = useAuthStore(selectAuthToken);
  const { selectedBranchId, setSelectedBranch } = useBranchContext();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  const [isDiscountModalOpen, setIsDiscountModalOpen] =
    useState<boolean>(false);

  const handleDiscountModalClose = (): void => {
    setIsDiscountModalOpen(false);
  };

  // Check if user is super admin
  const isSuperAdmin = (user?.role === "SUPER_ADMIN" || user?.role === "MULTI_BRANCH_ADMIN");
  // Get branch name - check both direct branch object and potential nested structure
  const branchName = user?.branch?.name || (user as any)?.branchName;

  // Initialize branch selection for both super admin and regular users
  useEffect(() => {
    if (isSuperAdmin) {
      fetchBranches();
    } else if (user?.branchId && !selectedBranchId) {
      // For non-super-admin users, automatically set their branch
      setSelectedBranch(user.branchId, user.branch?.name || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, user?.branchId, user?.branch?.name, selectedBranchId]);

  const fetchBranches = async () => {
    if (!token) {
      toast.error("Необходима авторизация");
      return;
    }

    try {
      setIsLoadingBranches(true);

      // Use centralized API configuration
      const url = buildApiUrl(`${API_ENDPOINTS.BRANCHES.LIST}?limit=100`);

      if (!url) {
        throw new Error("API URL is not available");
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        await handleHttpError(response, {
          401: "Необходима повторная авторизация",
          403: "Нет доступа к списку филиалов",
        });
        return;
      }

      const data = await response.json();

      // Normalize response structure - prefer canonical format
      let branchList: Branch[] = [];
      if (Array.isArray(data?.branches)) {
        branchList = data.branches;
      } else if (Array.isArray(data?.data?.branches)) {
        branchList = data.data.branches;
      } else if (Array.isArray(data?.data)) {
        branchList = data.data;
      } else {
        console.warn("Unexpected branches response format:", data);
        toast.error("Некорректный формат данных филиалов");
        return;
      }

      setBranches(branchList);

      // Auto-select first branch if none is selected (for super admin)
      if (branchList.length > 0 && !selectedBranchId) {
        const firstBranch = branchList[0];
        if (firstBranch) {
          setSelectedBranch(firstBranch.id, firstBranch.name);
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      toast.error("Ошибка при загрузке списка филиалов");
    } finally {
      setIsLoadingBranches(false);
    }
  };

  const handleBranchChange = (branchId: string) => {
    const selected = branches.find((b) => b.id === branchId);
    setSelectedBranch(branchId, selected?.name || null);
  };

  return (
    <>
      <header className={`header header--${isDarkMode ? "dark" : "light"}`}>
        <div className="header__content">
          <div className="header__right">
            {/* Show branch selector for super admin */}
            {isSuperAdmin ? (
              <div className="header__branch-selector">
                <label className="header__branch-label">Филиал:</label>
                <select
                  className="header__branch-select"
                  value={selectedBranchId || ""}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  disabled={isLoadingBranches}
                >
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              /* Show branch info only for non-super-admin users */
              branchName && (
                <div className="header__branch-info">
                  <span className="header__branch-label">Филиал:</span>
                  <span className="header__branch-name" title={branchName}>
                    {branchName}
                  </span>
                </div>
              )
            )}

            <button
              className={`header__theme-toggle header__theme-toggle--${
                isDarkMode ? "dark" : "light"
              }`}
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              <Icon name={isDarkMode ? "moon" : "sun"} size="medium" />
            </button>

            <div className="header__profile">
              <button
                className="header__profile-btn"
                aria-label="Profile"
                title="Выйти из системы"
              >
                {user?.email?.slice(0, 1).toUpperCase() ||
                  user?.telegramUsername?.slice(0, 1).toUpperCase() ||
                  "U"}
              </button>
            </div>
          </div>
        </div>
      </header>

      <DiscountModal
        isOpen={isDiscountModalOpen}
        onClose={handleDiscountModalClose}
        products={products}
        onUpdate={onProductsUpdate}
      />
    </>
  );
};

export default Header;
